<template>
    <div class="initiator-picker">
        <div v-if="searchable && tiles.length > searchThreshold" class="initiator-search">
            <span class="initiator-search-icon"><i class="fe fe-search"></i></span>
            <input
                v-model="search"
                type="search"
                class="form-control"
                :placeholder="searchPlaceholder"
            />
            <span class="initiator-search-count">
                {{ search ? `${filteredTiles.length} of ${tiles.length}` : `${tiles.length} available` }}
            </span>
        </div>

        <div v-if="filteredTiles.length" class="initiator-grid">
            <div
                v-for="tile in filteredTiles"
                :key="tile.value"
                class="card initiator-tile"
                :class="[currentTile == tile.value ? 'active' : '']"
                @click="tileClick(tile.value)"
            >
                <span v-show="currentTile == tile.value" class="badge"><i class="fa fa-check"></i></span>
                <div class="card-body">
                    <div class="initiator-logo">
                        <img
                            v-if="logoSrc(tile) && !brokenLogos.includes(tile.value)"
                            :src="logoSrc(tile)"
                            :alt="tile.title"
                            loading="lazy"
                            @error="onLogoError(tile)"
                        />
                        <span v-else class="initiator-logo-fallback">{{ initials(tile.title) }}</span>
                    </div>
                    <h4 class="card-title text-center mb-1">{{ tile.title }}</h4>
                    <h5
                        v-if="tile.subTitle"
                        class="card-subtitle text-center text-muted initiator-desc"
                        :title="tile.subTitle"
                    >
                        {{ tile.subTitle }}
                    </h5>
                </div>
            </div>
        </div>
        <div v-else class="initiator-empty">
            <!--Note: the wizard narrows the tile list by tag type before the search
                box is involved, so an empty grid is not always a failed search-->
            <span v-if="search">No initiators match "{{ search }}".</span>
            <span v-else>{{ emptyHint || "No initiators available." }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: "InitiatorPicker",
    props: {
        // Tiles shaped as { title, subTitle, value, iconSrc } - same contract as Tiles.vue
        tiles: {
            type: Array,
            default: () => []
        },
        selectedTile: [String, Boolean],
        searchable: {
            type: Boolean,
            default: true
        },
        searchPlaceholder: {
            type: String,
            default: "Search initiators..."
        },
        searchThreshold: {
            type: Number,
            default: 6
        },
        // Shown instead of the no-match copy when the grid is empty for a reason
        // other than the search box (e.g. no initiator carries the chosen tag type)
        emptyHint: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            currentTile: this.selectedTile,
            search: "",
            brokenLogos: []
        }
    },
    watch: {
        selectedTile(val) {
            this.currentTile = val;
        }
    },
    computed: {
        filteredTiles() {
            if (!this.search) return this.tiles;
            const term = this.search.toLowerCase();
            return this.tiles.filter((tile) => {
                return `${tile.title || ""} ${tile.subTitle || ""}`.toLowerCase().includes(term);
            });
        }
    },
    methods: {
        tileClick(val) {
            this.currentTile = this.currentTile == val ? null : val;
            this.$emit('onSelectTile', this.currentTile);
        },
        logoSrc(tile) {
            const src = tile.iconSrc;
            if (!src) return null;
            if (src.includes('base64')) return src;
            try {
                return require("../../assets/" + src);
            } catch (e) {
                return null;
            }
        },
        onLogoError(tile) {
            if (!this.brokenLogos.includes(tile.value)) this.brokenLogos.push(tile.value);
        },
        initials(title) {
            return (title || "")
                .split(/[\s\-&/]+/)
                .filter((word) => /^[a-z]/i.test(word))
                .slice(0, 3)
                .map((word) => word[0].toUpperCase())
                .join("");
        }
    },
    emits: ['onSelectTile']
}
</script>

<style scoped>
    .initiator-picker {
        --ip-ink: #12263F;
        --ip-ink-soft: #6E84A3;
        --ip-ink-faint: #95AAC9;
        --ip-line: #E3EBF6;
        --ip-surface: #ffffff;
        --ip-logo-bg: #F9FBFD;
        --ip-accent: #2C7BE5;
        --ip-accent-ink: #1657B0;
        --ip-accent-wash: rgba(44, 123, 229, 0.07);
        --ip-ring: rgba(44, 123, 229, 0.16);
        --ip-shadow: rgba(18, 38, 63, 0.16);
    }

    /* Search sits inline with a live match count so a long list of platforms
       stays navigable without a second row of chrome */
    .initiator-search {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        max-width: 24rem;
        margin-bottom: 1.125rem;
    }

    .initiator-search-icon {
        position: absolute;
        left: 0.6875rem;
        display: flex;
        font-size: 0.875rem;
        color: var(--ip-ink-faint);
        pointer-events: none;
    }

    .initiator-search .form-control {
        padding-left: 2.125rem;
        font-size: 0.8125rem;
    }

    .initiator-search-count {
        flex: 0 0 auto;
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--ip-ink-faint);
        white-space: nowrap;
    }

    .initiator-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr));
        gap: 0.5rem;
        margin: 0;
    }

    .initiator-tile {
        position: relative;
        margin-bottom: 0;
        padding: 0;
        cursor: pointer;
        background-color: var(--ip-surface);
        border: 1px solid var(--ip-line);
        border-radius: 0.5rem;
        box-shadow: none;
        transition: border-color 0.16s ease, box-shadow 0.16s ease,
            transform 0.16s ease, background-color 0.16s ease;
    }

    .initiator-tile:hover {
        border-color: #B1C2D9;
        box-shadow: 0 8px 18px -12px var(--ip-shadow);
        transform: translateY(-1px);
    }

    .initiator-tile.active {
        border-color: var(--ip-accent);
        background-color: var(--ip-accent-wash);
        box-shadow: 0 0 0 3px var(--ip-ring);
    }

    .initiator-tile .card-body {
        padding: 0.75rem 0.625rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .initiator-tile .card-title {
        font-size: 0.75rem;
        font-weight: 600;
        line-height: 1.3;
        color: var(--ip-ink);
    }

    .initiator-tile.active .card-title {
        color: var(--ip-accent-ink);
    }

    .initiator-logo {
        height: 2.25rem;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--ip-logo-bg);
        border: 1px solid var(--ip-line);
        border-radius: 0.375rem;
        padding: 0.25rem 0.5rem;
        overflow: hidden;
    }

    .initiator-logo img {
        width: 100%;
        height: 1.625rem;
        object-fit: contain;
    }

    .initiator-logo-fallback {
        font-weight: 600;
        font-size: 0.8125rem;
        letter-spacing: 0.04em;
        color: var(--ip-ink-faint);
    }

    .initiator-desc {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin-bottom: 0;
        font-size: 0.625rem;
        line-height: 1.35;
        color: var(--ip-ink-soft) !important;
    }

    /* The check reads as a chip on the tile corner rather than a floating badge */
    .badge {
        position: absolute;
        top: 0.375rem;
        right: 0.375rem;
        z-index: 2;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.125rem;
        height: 1.125rem;
        padding: 0;
        font-size: 0.5rem;
        color: #ffffff;
        background-color: var(--ip-accent);
        border: 0;
        border-radius: 50%;
        box-shadow: none;
    }

    .initiator-empty {
        padding: 2.5rem 0;
        text-align: center;
        font-size: 0.8125rem;
        color: var(--ip-ink-faint);
    }

    @media (prefers-reduced-motion: reduce) {
        .initiator-tile {
            transition: none;
        }

        .initiator-tile:hover {
            transform: none;
        }
    }
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode .initiator-picker {
    --ip-ink: #{$dark-text-primary};
    --ip-ink-soft: #{$dark-text-secondary};
    --ip-ink-faint: #{$dark-text-muted};
    --ip-line: #{$dark-border-color};
    --ip-surface: #{$dark-input-bg};
    --ip-logo-bg: #{$dark-elevated-bg};
    --ip-accent: #4d90ea;
    --ip-accent-ink: #a8caf8;
    --ip-accent-wash: rgba(77, 144, 234, 0.15);
    --ip-ring: rgba(77, 144, 234, 0.24);
    --ip-shadow: rgba(0, 0, 0, 0.55);

    .initiator-tile:hover {
        border-color: #3d434c;
    }
}
</style>
